import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useState } from "react";
import { useAppStore } from "@/store";
import {
  MDSRowItem,
  MDSRowItemSlotName,
  MDSRowItemAppearance,
} from "@/mds/components/lists/row-item";
import { MDSTooltip } from "@/mds/components/tooltip";
import { MDSTooltipPlacement } from "@/mds/components/tooltip/tooltip.types";
import { MDSText, MDSTextVariant, MDSTextWeight, MDSTextColor } from "@/mds/components/text";
import { MDSIconButton, MDSIconButtonVariant } from "@/mds/components/buttons/icon-button";
import { MDSIcon } from "@/mds/components/icon/icon";
import { MDSIconKind, MDSIconColor } from "@/mds/components/icon/icon/icon.types";
import { MDSCollectionChip } from "@/mds/components/chips/collection-chip";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import type { ImportableNote } from "@/store/one-click-import/types";
import type { FC } from "react";
import { formatRelativeUpdateTime } from "@/components/heads-up/sidebar/helpers/format-relative-time";

interface NoteListItemProps {
  note: ImportableNote;
}

export const NoteListItem: FC<NoteListItemProps> = observer(function NoteListItem({ note }) {
  const { store } = useAppStore();
  const [isHovered, setIsHovered] = useState(false);

  const toggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!note.isLocked) {
      const wasSelected = note.isSelected;
      const totalCount = store.oneClickImport.totalNotesCount;

      store.oneClickImport.toggleNoteSelection(note.id);

      // Track after toggle so counts reflect the new state
      const selectedCount = store.oneClickImport.selectedNotesCount;

      if (wasSelected) {
        trackEvent(TrackedEvent.OneClickImportItemDeselect, {
          note_title: note.title,
          note_subtitle: note.subtitle,
          selected_count: selectedCount,
          total_count: totalCount,
        });
      } else {
        trackEvent(TrackedEvent.OneClickImportItemSelect, {
          note_title: note.title,
          note_subtitle: note.subtitle,
          selected_count: selectedCount,
          total_count: totalCount,
        });
      }
    }
  };

  const getIconKind = () => {
    if (note.isLocked) {
      return MDSIconKind.Document;
    }
    if (note.isSelected) {
      return MDSIconKind.CheckboxSolid;
    }
    if (isHovered) {
      return MDSIconKind.CheckboxEmpty;
    }
    return MDSIconKind.Document;
  };

  const rowContent = (
    <StyledNoteItemWrapper>
      <StyledMDSRowItem
        $isSelected={!note.isLocked && note.isSelected}
        $isHighlighted={isHovered}
        $isSpecial={false}
        $appearance={
          note.isLocked
            ? MDSRowItemAppearance.AudioRecordingProcessingFailed
            : MDSRowItemAppearance.Standard
        }
        $mustClickOnRowTitle={false}
        noSeparator={false}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <StyledLeftAddon slot={MDSRowItemSlotName.LeftAddon}>
          <StyledIconButton
            onClick={toggleSelection}
            variant={MDSIconButtonVariant.Transparent}
            iconKind={getIconKind()}
            $isSelected={!note.isLocked && note.isSelected}
            $isHighlighted={isHovered && !note.isLocked}
            $isLocked={note.isLocked}
          />
        </StyledLeftAddon>

        <StyledContentRow>
          <StyledContent>
            <StyledTitle $isLocked={note.isLocked}>
              {note.isPinned && (
                <StyledPinIcon>
                  <MDSIcon
                    kind={MDSIconKind.ThumbtackSolid}
                    size={12}
                    color={MDSIconColor.Secondary}
                  />
                </StyledPinIcon>
              )}
              <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Medium}>
                {note.title}
              </MDSText>

              {store.oneClickImport.getBadgesForNote(note).map(badge => (
                <MDSCollectionChip
                  key={`${badge.type}-${badge.name}`}
                  collectionId={badge.collectionId}
                  label={badge.name}
                  inline
                />
              ))}
            </StyledTitle>
          </StyledContent>

          <StyledDateContainer>
            {note.updatedAt && (
              <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                {formatRelativeUpdateTime(note.updatedAt, "")}
              </MDSText>
            )}
          </StyledDateContainer>
        </StyledContentRow>

        <StyledBottomRow slot={MDSRowItemSlotName.BottomAddon}>
          {note.subtitle && (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              {note.subtitle}
            </MDSText>
          )}
        </StyledBottomRow>
      </StyledMDSRowItem>
    </StyledNoteItemWrapper>
  );

  if (note.isLocked) {
    return (
      <MDSTooltip
        config={{
          label:
            note.lockedReason || "This note is locked in Apple Notes. Please unlock it to import.",
          placement: MDSTooltipPlacement.Top,
        }}
      >
        {rowContent}
      </MDSTooltip>
    );
  }

  return rowContent;
});

const StyledMDSRowItem = styled(MDSRowItem)`
  padding-inline: ${({ theme }) => theme.spacing["2"]};
`;
const StyledNoteItemWrapper = styled.div`
  border-top: 1px solid transparent;
`;

const StyledLeftAddon = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  min-width: fit-content;
  align-self: stretch;
  padding-right: ${({ theme }) => theme.spacing["3"]};
  padding-top: 6px;
`;

const StyledIconButton = styled(MDSIconButton)<{
  $isSelected?: boolean;
  $isHighlighted?: boolean;
  $isLocked?: boolean;
}>`
  padding: 0px;
  height: 16px;
  width: 16px;

  .mds-icon {
    font-size: 18px;
    height: 18px;
    width: 18px;
    color: ${({ $isSelected, $isHighlighted, $isLocked, theme }) =>
      $isLocked
        ? theme.colors.icon.danger
        : $isSelected
          ? theme.colors.icon.selected
          : $isHighlighted
            ? theme.colors.icon.placeholder
            : theme.colors.icon.placeholder};
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  ${({ $isLocked }) =>
    $isLocked &&
    `
    cursor: not-allowed;
  `}
`;

const StyledContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const StyledContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const StyledTitle = styled.div<{ $isLocked: boolean }>`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledPinIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  min-width: 0;
  margin-left: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const StyledBottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
  min-height: 20px;
`;
